import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Flex,
  Spinner,
  Stack,
  Tag,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { Account, FreightService, RequireOnly } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi2';

import { trpc } from '../config/trpc';
import { useProvider } from '../hooks/providers/useProvider';
import { useQuote } from '../hooks/quote/useQuote';
import { useFieldValidationStates } from '../hooks/useFieldValidationStates';
import { quoteStatusColorScheme } from '../utils/constants';
import { AirlineIcon } from './Icons/AirlineIcon';
import { TruckIcon } from './Icons/TruckIcon';
import { LabeledText } from './LabeledText';
import {
  SimpleContactSearch,
  SimpleContactSearchForm,
  SimpleContactSearchProps,
  SimpleContactSearchSelected,
} from './SimpleContactSearch';

type SimpleTruckRecipientFormProps = Omit<SimpleContactSearchProps, 'name' | 'children'> & {
  defaultAccount?: RequireOnly<Account, 'id' | 'name'>;
  prefix: string;
  airportId?: string;
};

export const getErrorMessage = (error: any) =>
  Object.values(error)
    .map((value: any) => {
      if (value?.message) return value?.message;
      return undefined;
    })
    .filter(Boolean)
    .join(', ');

const getBorderColor = ({ hasError, hasWarning, isExpanded }) => {
  if (hasError) return 'red.500';
  if (hasWarning) return 'orange.500';
  if (isExpanded) return;
  return 'transparent';
};

export const SimpleTruckingRecipientForm = ({
  prefix,
  defaultAccount,
  ...contactSearchProps
}: SimpleTruckRecipientFormProps) => {
  const { id, isExpanded, originAirportId, recipients, quoteId } = useWatch({
    name: prefix,
  });
  const hasNonTruckRecipients = useMemo(() => {
    return recipients?.some((recipient) => recipient?.provider_id !== id);
  }, [recipients, id]);

  const { error = {}, isInvalid } = useFieldValidationStates(prefix as string);
  const errorMessages = getErrorMessage(error);

  const borderColor = getBorderColor({
    hasError: isInvalid,
    hasWarning: hasNonTruckRecipients,
    isExpanded,
  });
  const searchDisclosure = useDisclosure({ defaultIsOpen: true });

  const scrollToRef = useRef<HTMLDivElement>();

  const scrollSearchIntoView = () => {
    if (scrollToRef.current) {
      (scrollToRef.current as any)?.scrollIntoViewIfNeeded({
        behavior: 'smooth',
      });
    }
  };

  const toggledIsExpanded = () => setValue(`${prefix}.isExpanded`, !isExpanded);
  const toggleShowSearch = () => {
    searchDisclosure.onToggle();
    if (!searchDisclosure.isOpen) setTimeout(scrollSearchIntoView, 100);
  };

  const { setValue } = useFormContext();
  const [truck, isTruckLoading] = useProvider(id);
  const [quote, isQuoteLoading] = useQuote(quoteId);

  const { data: fulfillmentPartner } = trpc.getFulfillmentPartner.useQuery(
    { id: quote?.fulfillment_partner_configuration_id },
    {
      enabled: !!quote?.fulfillment_partner_configuration_id,
      refetchOnWindowFocus: false,
    },
  );

  const bgColorHover = useColorModeValue('gray.50', 'gray.600');
  const avatarOpacity = useColorModeValue(1, 0.4);
  const colorScheme = useMemo(
    () => (quote?.status ? quoteStatusColorScheme[quote.status] : undefined),
    [quote?.status],
  );

  const defaultIcon = useMemo(() => {
    const freightService = quote?.freight_service;
    if (freightService === FreightService.AFR) return <AirlineIcon />;
    if (freightService === FreightService.EXW) return <TruckIcon />;
    if (freightService === FreightService.FCA) return <TruckIcon />;
    return <AirlineIcon />;
  }, [quote?.freight_service]);

  if (isTruckLoading) {
    return (
      <Stack borderTopWidth={1} direction="row" justifyItems="center" alignItems="center" py={6} w="full">
        <Spinner size="md" />;
      </Stack>
    );
  }

  return (
    <Flex mb={2} key={truck?.id} direction="column" borderWidth={1} borderColor={borderColor} borderRadius="lg">
      <SimpleContactSearch
        name={`${prefix}.recipients`}
        {...contactSearchProps}
        defaultAccounts={[defaultAccount]}
        defaultAirportIds={[originAirportId]}
        defaultProviderIds={[id]}
        searchPattern="AIRLINE"
      >
        <Stack
          direction="row"
          alignItems="center"
          justify="space-between"
          py={6}
          px={4}
          spacing={4}
          w="full"
          onClick={toggledIsExpanded}
          borderRadius="lg"
          _hover={{ bgColor: bgColorHover }}
          userSelect="none"
        >
          <Stack direction="row" spacing="4" align="center">
            <Checkbox colorScheme="brand" size="lg" isChecked={isExpanded} pointerEvents="none" />
            <Avatar
              src={truck?.logo_uri}
              borderRadius="sm"
              aria-label={`${truck?.id} Logo`}
              icon={defaultIcon}
              boxSize="30px"
              opacity={avatarOpacity}
            />
            <Text>{truck?.name}</Text>
          </Stack>
          <Stack spacing={0} justify="center">
            <Show if={!!error}>
              <Text color="red.500" fontSize="xs">
                {errorMessages}
              </Text>
            </Show>
            <Show if={hasNonTruckRecipients}>
              <Text color="orange.500" fontSize="xs">
                Caution! Recipients from other Trucks are included.
              </Text>
            </Show>
          </Stack>
          <Show if={isQuoteLoading}>
            <Spinner size="sm" />
          </Show>
          <Show if={!!fulfillmentPartner}>
            <LabeledText
              value={fulfillmentPartner?.account_as_fulfillment_partner?.name}
              textProps={{ noOfLines: 1 }}
            />
          </Show>
          <Show if={!isQuoteLoading}>
            <Tag colorScheme={colorScheme} size="sm" borderRadius="md">
              {quote?.status}
            </Tag>
          </Show>
        </Stack>
        <Stack p={4} pt={1} hidden={!isExpanded}>
          <SimpleContactSearchSelected />

          <Button
            variant="ghost"
            colorScheme="brand"
            size="sm"
            rightIcon={searchDisclosure.isOpen ? <HiChevronUp /> : <HiChevronDown />}
            onClick={toggleShowSearch}
          >
            {searchDisclosure.isOpen ? 'Minimize Search' : 'Expand Search'}
          </Button>

          <SimpleContactSearchForm borderWidth={1} p={3} borderRadius="md" hidden={!searchDisclosure.isOpen} />
          <Box ref={scrollToRef} />
        </Stack>
      </SimpleContactSearch>
    </Flex>
  );
};
